import { BusinessObjectBase } from './base';
import { sessionActivityItemSchema } from '@schemas/business-objects/session-activity-item';
import type { ActivityItem } from './activity-item';
import type { Resource } from './resource';
import type { Session } from './session';
import type { Id } from '../base-types';

export interface SessionActivityItemData {
  readonly id: Id;
  readonly textValue?: string;
  readonly numberValue?: number;
  readonly sessionId: Id;
  readonly session?: Session;
  readonly activityItemId: Id;
  readonly activityItem?: ActivityItem;
  readonly resourceId: Id;
  readonly resource?: Resource;
}

export interface SessionActivityItem extends Readonly<SessionActivityItemData> {}
type SessionActivityItemCreateWithChildrenData =
  Partial<SessionActivityItemData>;

export class SessionActivityItem extends BusinessObjectBase {
  static readonly entityName = 'SessionActivityItem' as const;
  static readonly plural = 'session-activity-items';
  static schema = sessionActivityItemSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SessionActivityItem[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SessionActivityItem(data));
  }

  static async findOne(filter?: any): Promise<SessionActivityItem | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SessionActivityItem(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<SessionActivityItem> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SessionActivityItem(data);
  }

  static async create(
    data: Omit<SessionActivityItemData, 'id'>,
  ): Promise<SessionActivityItem> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SessionActivityItem(result);
  }

  static async createMany(
    data: Omit<SessionActivityItemData, 'id'>[],
  ): Promise<SessionActivityItem[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SessionActivityItem(item));
  }

  static async createWithChildren(
    data: SessionActivityItemCreateWithChildrenData,
  ): Promise<SessionActivityItem> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new SessionActivityItem(result);
  }

  static async createManyWithChildren(
    data: SessionActivityItemCreateWithChildrenData[],
  ): Promise<SessionActivityItem[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SessionActivityItem(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<SessionActivityItemData>,
  ): Promise<SessionActivityItem> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SessionActivityItem(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
